import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  describeOtpSendError,
  describeOtpVerifyError,
  isGatewayTimeout,
  isRateLimitError,
  rateLimitRetryAfterSeconds,
  useSendOtp,
  useVerifyOtp,
} from "../api/auth-api";
import { BrandMark, LogoMark } from "../components/dss/LogoMark";
import { Eyebrow, Field, PrimaryButton } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";

// GoTrue refuses a second mail to the same address inside its minimum
// send interval (60s on Supabase's built-in mailer). Counting down to it in
// the UI is friendlier than letting "Send igen" fire into a rate-limit
// error — which is what people do when the first code doesn't turn up.
const RESEND_COOLDOWN_SECONDS = 60;

// How long to hold the buttons after a 429 that names no wait of its own.
// That case is the project's hourly mail budget, which does not clear on a
// retry and may not clear for the rest of the hour — so this is a "stop
// hammering it" interval, not a promise that it will work afterwards. A
// per-address throttle names its own number and we use that instead.
const RATE_LIMIT_COOLDOWN_SECONDS = 5 * 60;

const formatCooldown = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Whether the current cooldown is the server blocking us (worth gating the
  // first send on too) rather than the ordinary gap after a code went out.
  const [isBlocked, setIsBlocked] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();

  const { mutateAsync: sendOtp, isLoading: isSending } = useSendOtp();
  const { mutateAsync: verifyOtp, isLoading: isVerifying } = useVerifyOtp();

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onSendFailed = useCallback((err: unknown) => {
    setError(describeOtpSendError(err));
    if (isRateLimitError(err)) {
      setCooldown(rateLimitRetryAfterSeconds(err) ?? RATE_LIMIT_COOLDOWN_SECONDS);
      setIsBlocked(true);
    }
  }, []);

  const onSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isBlocked && cooldown > 0) return;
    setError(null);
    setNotice(null);
    try {
      await sendOtp(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setIsBlocked(false);
      setStep("otp");
      setTimeout(() => inputsRef.current[0]?.focus(), 0);
    } catch (err) {
      // A timed-out request may still have mailed a code (see
      // isGatewayTimeout). Go to the code screen and say so, rather than
      // insisting nothing was sent while it sits in their inbox.
      if (isGatewayTimeout(err)) {
        setNotice(describeOtpSendError(err));
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setStep("otp");
        setTimeout(() => inputsRef.current[0]?.focus(), 0);
        return;
      }
      onSendFailed(err);
    }
  };

  // A different address is a different rate-limit bucket, and re-reading a
  // stale "wait 5 minutes" while typing a new one is just wrong.
  const onEmailChange = (next: string) => {
    setEmail(next);
    if (isBlocked) {
      setIsBlocked(false);
      setCooldown(0);
      setError(null);
    }
  };

  // Previously `onClick={() => sendOtp(email)}`: the promise was dropped on
  // the floor, so a rejected resend — a rate limit, most often — showed the
  // user nothing whatsoever, and neither did a successful one.
  const onResend = useCallback(async () => {
    if (cooldown > 0 || isSending) return;
    setError(null);
    setNotice(null);
    try {
      await sendOtp(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice("Ny kode sendt.");
    } catch (err) {
      if (isGatewayTimeout(err)) {
        setNotice(describeOtpSendError(err));
        setCooldown(RESEND_COOLDOWN_SECONDS);
        return;
      }
      onSendFailed(err);
    }
  }, [cooldown, email, isSending, onSendFailed, sendOtp]);

  const onDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const onDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    try {
      await verifyOtp({ email, token: code.join("") });
      navigate("/");
    } catch (err) {
      setError(describeOtpVerifyError(err));
    }
  };

  if (step === "otp") {
    return (
      <div
        className="mx-auto max-w-md min-h-app bg-paper px-8 pb-8"
        style={{ paddingTop: "calc(2rem + env(safe-area-inset-top))" }}
      >
        <button
          onClick={() => setStep("email")}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>Ret mail</span>
        </button>
        <div className="font-display text-[46px] leading-[0.9] tracking-tight font-extrabold mt-8 uppercase">
          Tjek din
          <br />
          mail<span className="text-sage">.</span>
        </div>
        <div className="text-[13.5px] text-ink2 mt-3 leading-relaxed">
          Vi sendte en 6-cifret kode til{" "}
          <span className="font-mono text-[12.5px] text-ink">{email}</span>
        </div>

        <form onSubmit={onVerify}>
          <div className="grid grid-cols-6 gap-2 mt-8">
            {code.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                value={digit}
                onChange={(e) => onDigitChange(i, e.target.value)}
                onKeyDown={(e) => onDigitKeyDown(i, e)}
                inputMode="numeric"
                // Lets iOS offer the emailed code straight from the keyboard
                // bar. Only the first box carries it — with six inputs, iOS
                // fills the focused one, so advertising it on all six would
                // offer the same autofill six times over.
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={1}
                // At 24px this is already well clear of iOS's 16px
                // zoom-on-focus threshold.
                className="h-[58px] rounded-lg bg-card border border-line-soft text-center font-display font-extrabold text-2xl tracking-tight focus:outline-none focus:border-ink"
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onResend}
            disabled={cooldown > 0 || isSending}
            className="mt-3.5 font-mono text-[10px] tracking-[1.3px] uppercase text-ink font-semibold disabled:text-ink3"
          >
            {cooldown > 0 ? `Send igen om ${formatCooldown(cooldown)}` : "Send igen"}
          </button>
          {notice && (
            <div className="mt-3 text-ink2 font-mono text-[11px] uppercase">{notice}</div>
          )}
          {error && <div className="mt-3 text-clay font-mono text-[11px] uppercase">{error}</div>}
          <div className="mt-8">
            <PrimaryButton
              type="submit"
              disabled={isVerifying}
              meta={
                <>
                  <span>VERIFICÉR</span>
                  <Icon name="arrow-right" size={13} />
                </>
              }
            >
              <span className="inline-flex items-center gap-2">
                <Icon name="unlock-keyhole" size={14} /> Log ind
              </span>
            </PrimaryButton>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-md min-h-app bg-paper px-8 pb-8"
      style={{ paddingTop: "calc(2rem + env(safe-area-inset-top))" }}
    >
      <div className="flex justify-center mb-6">
        <LogoMark size={124} />
      </div>
      <BrandMark mark={false} />
      <div className="font-display text-[58px] leading-[0.88] tracking-tight font-extrabold mt-8 uppercase">
        Log ind<span className="text-sage">.</span>
      </div>
      <div className="text-[13.5px] text-ink2 mt-3 leading-relaxed max-w-[290px]">
        Alle medlemmer har adgang. Du får en engangskode på mail — ingen adgangskode at huske.
      </div>

      <form onSubmit={onSendCode}>
        <div className="mt-8">
          <Field label="E-mail" htmlFor="email">
            <div className="flex items-center gap-2.5 bg-card border border-ink rounded-lg py-3.5 px-3.5">
              <Icon name="envelope" variant="regular" size={14} className="text-ink3" />
              <input
                id="email"
                // `autoComplete` alone isn't enough: browsers and password
                // managers key their autofill heuristics off name/id and the
                // associated <label> too, so a field missing those gets
                // skipped even with the right autocomplete token.
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="dig@eksempel.dk"
                autoComplete="email"
                // Deliberately NOT autoCorrect="off" / spellCheck={false}:
                // iOS delivers Text Replacement shortcuts (the ones people set
                // up to expand into their own address) through the same
                // autocorrection pipeline, so disabling it stops those
                // shortcuts expanding here. autoCapitalize is enough on its
                // own to stop the address being capitalised, and type="email"
                // already keeps iOS from "correcting" address-shaped text.
                autoCapitalize="none"
                // 16px is the threshold below which iOS Safari zooms the page
                // in when the field takes focus — the single most "this is a
                // website" tell in an installed PWA.
                className="flex-1 bg-transparent font-mono text-base font-medium focus:outline-none"
              />
            </div>
          </Field>
        </div>
        {error && <div className="mt-3 text-clay font-mono text-[11px] uppercase">{error}</div>}
        <div className="mt-6">
          <PrimaryButton
            type="submit"
            disabled={isSending || (isBlocked && cooldown > 0)}
            meta={
              <>
                <span>{isBlocked && cooldown > 0 ? formatCooldown(cooldown) : "SEND KODE"}</span>
                <Icon name="arrow-right" size={13} />
              </>
            }
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="paper-plane" size={14} /> Send engangskode
            </span>
          </PrimaryButton>
        </div>
        <Eyebrow className="text-center mt-4">
          Første gang du logger ind, vælger du hvem du er.
        </Eyebrow>
      </form>
    </div>
  );
};

export default LoginPage;
